/**
 * Token Manager Utility
 * Centralized management for JWT tokens including validation, expiration checking,
 * refresh logic, and session management.
 */

// Token storage keys
const TOKEN_KEY = 'token';
const LAST_ACTIVITY_KEY = 'lastActivity';
const KEEP_SIGNED_IN_KEY = 'keepSignedIn';

// Configuration constants
const TOKEN_EXPIRY_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes before expiry
const SESSION_TIMEOUT_MS = 12 * 60 * 60 * 1000; // 12 hours of inactivity
const ACTIVITY_WRITE_THROTTLE_MS = 60 * 1000; // at most one activity write per minute
const REFRESH_ENDPOINT = '/api/auth/refresh-token';

// State for managing concurrent refresh requests
let isRefreshing = false;
let refreshSubscribers = [];

/**
 * Subscribe to token refresh completion
 * @param {Function} callback - Callback to execute when refresh completes
 */
function subscribeToRefresh(callback) {
  refreshSubscribers.push(callback);
}

/**
 * Notify all subscribers that refresh is complete
 * @param {string|null} newToken - The new token, or null if refresh failed
 */
function notifyRefreshSubscribers(newToken) {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
}

/**
 * Get token from localStorage
 * @returns {string|null} The stored token or null
 */
export function getToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (error) {
    console.error('Error reading token from localStorage:', error);
    return null;
  }
}

/**
 * Save token to localStorage
 * @param {string} token - The JWT token to store
 */
export function setToken(token) {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    try {
      localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
    } catch (activityError) {
      console.error('Error updating activity timestamp:', activityError);
    }
  } catch (error) {
    console.error('Error saving token to localStorage:', error);
  }
}

/**
 * Remove token from localStorage
 */
export function clearToken() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(LAST_ACTIVITY_KEY);
  } catch (error) {
    console.error('Error clearing token from localStorage:', error);
  }
}

/**
 * Validate JWT token structure
 * @param {string} token - The token to validate
 * @returns {boolean} True if token has valid JWT structure
 */
export function isValidTokenStructure(token) {
  if (!token || typeof token !== 'string') {
    return false;
  }

  const parts = token.split('.');
  if (parts.length !== 3) {
    return false;
  }

  try {
    // Try to decode each part (header and payload)
    atob(parts[0].replace(/-/g, '+').replace(/_/g, '/'));
    atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Decode JWT token payload
 * @param {string} token - The JWT token
 * @returns {Object|null} The decoded payload or null if invalid
 */
export function decodeToken(token) {
  if (!isValidTokenStructure(token)) {
    return null;
  }

  try {
    const parts = token.split('.');
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const decoded = JSON.parse(atob(payload));
    return decoded;
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
}

/**
 * Check if token is expired
 * @param {string} token - The JWT token
 * @returns {boolean} True if token is expired
 */
export function isTokenExpired(token) {
  const payload = decodeToken(token);
  if (!payload) {
    return true;
  }

  // If no exp claim, consider it expired for safety
  if (!payload.exp) {
    return true;
  }

  const currentTime = Math.floor(Date.now() / 1000);
  return payload.exp <= currentTime;
}

/**
 * Check if token expires soon (within threshold)
 * @param {string} token - The JWT token
 * @param {number} thresholdMs - Threshold in milliseconds (default: 5 minutes)
 * @returns {boolean} True if token expires within threshold
 */
export function isTokenExpiringSoon(token, thresholdMs = TOKEN_EXPIRY_THRESHOLD_MS) {
  const payload = decodeToken(token);
  if (!payload) {
    return true;
  }

  if (!payload.exp) {
    return true;
  }

  const currentTime = Date.now();
  const expiryTime = payload.exp * 1000; // Convert to milliseconds
  return (expiryTime - currentTime) <= thresholdMs;
}

/**
 * Get time until token expires
 * @param {string} token - The JWT token
 * @returns {number} Milliseconds until expiry, or 0 if expired
 */
export function getTimeUntilExpiry(token) {
  const payload = decodeToken(token);
  if (!payload || !payload.exp) {
    return 0;
  }

  const currentTime = Date.now();
  const expiryTime = payload.exp * 1000;
  const timeLeft = expiryTime - currentTime;
  return Math.max(0, timeLeft);
}

/**
 * Validate token completely (structure + expiration)
 * @param {string} token - The JWT token
 * @returns {boolean} True if token is valid and not expired
 */
export function isTokenValid(token) {
  if (!isValidTokenStructure(token)) {
    return false;
  }

  if (isTokenExpired(token)) {
    return false;
  }

  return true;
}

/**
 * Get user type from token
 * @param {string} token - The JWT token
 * @returns {string|null} The user type or null
 */
export function getUserTypeFromToken(token) {
  const payload = decodeToken(token);
  return payload?.user_type || null;
}

/**
 * Get user ID from token
 * @param {string} token - The JWT token
 * @returns {string|null} The user ID or null
 */
export function getUserIdFromToken(token) {
  const payload = decodeToken(token);
  return payload?.id || null;
}

/**
 * Update last activity timestamp
 */
export function updateLastActivity() {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
  } catch (error) {
    console.error('Error updating last activity:', error);
  }
}

/**
 * Get last activity timestamp
 * @returns {number|null} Last activity timestamp or null
 */
export function getLastActivity() {
  try {
    const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
    return lastActivity ? parseInt(lastActivity, 10) : null;
  } catch (error) {
    console.error('Error getting last activity:', error);
    return null;
  }
}

/**
 * Check if session has timed out due to inactivity
 * @param {number} timeoutMs - Timeout in milliseconds (default: 12 hours)
 * @returns {boolean} True if session has timed out
 */
export function isSessionTimedOut(timeoutMs = SESSION_TIMEOUT_MS) {
  const keepSignedIn = localStorage.getItem(KEEP_SIGNED_IN_KEY) === 'true';
  if (keepSignedIn) {
    return false;
  }

  const lastActivity = getLastActivity();
  if (!lastActivity) {
    return false; // No activity recorded, don't timeout
  }

  const timeSinceActivity = Date.now() - lastActivity;
  return timeSinceActivity >= timeoutMs;
}

/**
 * Set keep signed in preference
 * @param {boolean} keep - Whether to keep user signed in
 */
export function setKeepSignedIn(keep) {
  try {
    if (keep) {
      localStorage.setItem(KEEP_SIGNED_IN_KEY, 'true');
    } else {
      localStorage.removeItem(KEEP_SIGNED_IN_KEY);
    }
  } catch (error) {
    console.error('Error setting keep signed in:', error);
  }
}

/**
 * Get keep signed in preference
 * @returns {boolean} Whether keep signed in is enabled
 */
export function getKeepSignedIn() {
  try {
    return localStorage.getItem(KEEP_SIGNED_IN_KEY) === 'true';
  } catch (error) {
    return false;
  }
}

/**
 * Refresh the authentication token
 * @param {string} apiUrl - The API base URL
 * @returns {Promise<string|null>} The new token or null if refresh failed
 */
export async function refreshToken(apiUrl) {
  const currentToken = getToken();

  if (!currentToken) {
    return null;
  }

  // If already refreshing, wait for the existing refresh to complete
  if (isRefreshing) {
    return new Promise((resolve) => {
      subscribeToRefresh(resolve);
    });
  }

  isRefreshing = true;

  try {
    const response = await fetch(`${apiUrl}${REFRESH_ENDPOINT}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${currentToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Refresh failed with status: ${response.status}`);
    }

    const data = await response.json();

    if (!data.token || !isValidTokenStructure(data.token)) {
      throw new Error('Invalid token received from refresh');
    }

    // Store the new token
    setToken(data.token);

    // Notify subscribers
    notifyRefreshSubscribers(data.token);

    return data.token;
  } catch (error) {
    console.error('Token refresh failed:', error);

    // Notify subscribers of failure
    notifyRefreshSubscribers(null);

    return null;
  } finally {
    isRefreshing = false;
  }
}

/**
 * Check if a refresh is currently in progress
 * @returns {boolean} True if refresh is in progress
 */
export function isRefreshInProgress() {
  return isRefreshing;
}

/**
 * Wait for ongoing refresh to complete
 * @returns {Promise<string|null>} The new token or null
 */
export function waitForRefresh() {
  if (!isRefreshing) {
    return Promise.resolve(getToken());
  }

  return new Promise((resolve) => {
    subscribeToRefresh(resolve);
  });
}

/** Google hides One Tap after a sign-in until the site records a sign-out. */
export function releaseGoogleOneTapPrompt() {
  if (typeof document !== 'undefined') {
    document.cookie = 'g_state=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
  }
  if (typeof window === 'undefined') return;
  window.google?.accounts?.id?.disableAutoSelect?.();
}

/**
 * Clear all authentication data
 */
export function clearAllAuthData() {
  clearToken();
  try {
    localStorage.removeItem(KEEP_SIGNED_IN_KEY);
  } catch (error) {
    console.error('Error clearing auth data:', error);
  }

  if (typeof window !== 'undefined') {
    releaseGoogleOneTapPrompt();
    window.dispatchEvent(new Event('userLoggedOut'));
  }
}

/**
 * Get token validation state
 * @returns {Object} Token validation state
 */
export function getTokenState() {
  const token = getToken();

  if (!token) {
    return {
      hasToken: false,
      isValid: false,
      isExpired: true,
      isExpiringSoon: true,
      userType: null,
      userId: null,
      timeUntilExpiry: 0,
    };
  }

  return {
    hasToken: true,
    isValid: isTokenValid(token),
    isExpired: isTokenExpired(token),
    isExpiringSoon: isTokenExpiringSoon(token),
    userType: getUserTypeFromToken(token),
    userId: getUserIdFromToken(token),
    timeUntilExpiry: getTimeUntilExpiry(token),
  };
}

/**
 * Initialize activity tracking listeners
 * @param {Function} onActivity - Callback when activity is detected
 * @returns {Function} Cleanup function to remove listeners
 */
export function initActivityTracking(onActivity) {
  const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll'];
  let lastWriteAt = 0;

  const handleActivity = () => {
    const now = Date.now();
    if (now - lastWriteAt >= ACTIVITY_WRITE_THROTTLE_MS) {
      lastWriteAt = now;
      updateLastActivity();
    }
    if (onActivity) {
      onActivity();
    }
  };

  activityEvents.forEach((event) => {
    window.addEventListener(event, handleActivity, { passive: true });
  });

  return () => {
    activityEvents.forEach((event) => {
      window.removeEventListener(event, handleActivity);
    });
  };
}

/**
 * Start periodic token validation
 * @param {Object} options - Options for periodic validation
 * @param {number} options.intervalMs - Check interval in milliseconds (default: 5 minutes)
 * @param {Function} options.onExpired - Callback when token is expired
 * @param {Function} options.onExpiringSoon - Callback when token is expiring soon
 * @param {Function} options.onSessionTimeout - Callback when session times out
 * @param {string} options.apiUrl - API URL for refresh
 * @returns {Function} Cleanup function to stop validation
 */
export function startPeriodicValidation({
  intervalMs = 5 * 60 * 1000,
  onExpired,
  onExpiringSoon,
  onSessionTimeout,
  apiUrl,
}) {
  const checkToken = async () => {
    if (isSessionTimedOut()) {
      if (onSessionTimeout) {
        onSessionTimeout();
      }
      return;
    }

    const token = getToken();
    if (!token) {
      return;
    }

    if (isTokenExpired(token)) {
      if (onExpired) {
        onExpired();
      }
      return;
    }

    if (isTokenExpiringSoon(token)) {
      if (onExpiringSoon) {
        onExpiringSoon();
      }

      // Attempt background refresh if API URL is provided
      if (apiUrl) {
        await refreshToken(apiUrl);
      }
    }
  };

  // Run immediately
  checkToken();

  const intervalId = setInterval(checkToken, intervalMs);

  return () => {
    clearInterval(intervalId);
  };
}

// Export configuration constants for testing
export const CONFIG = {
  TOKEN_EXPIRY_THRESHOLD_MS,
  SESSION_TIMEOUT_MS,
  REFRESH_ENDPOINT,
  TOKEN_KEY,
  LAST_ACTIVITY_KEY,
  KEEP_SIGNED_IN_KEY,
};

export default {
  getToken,
  setToken,
  clearToken,
  isValidTokenStructure,
  decodeToken,
  isTokenExpired,
  isTokenExpiringSoon,
  getTimeUntilExpiry,
  isTokenValid,
  getUserTypeFromToken,
  getUserIdFromToken,
  updateLastActivity,
  getLastActivity,
  isSessionTimedOut,
  setKeepSignedIn,
  getKeepSignedIn,
  refreshToken,
  isRefreshInProgress,
  waitForRefresh,
  clearAllAuthData,
  getTokenState,
  initActivityTracking,
  startPeriodicValidation,
  CONFIG,
};
